import { createElement, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AtSign,
  Bell,
  Book,
  BookOpen,
  Briefcase,
  Building,
  Building2,
  Bus,
  Calendar,
  Camera,
  Car,
  Church,
  Cloud,
  Code,
  Coffee,
  Compass,
  Crown,
  ExternalLink,
  FileText,
  Flag,
  Gift,
  Globe,
  Hash,
  Headphones,
  Heart,
  Home,
  Image,
  Images,
  Landmark,
  Link,
  Link2,
  Mail,
  Map,
  MapPin,
  Megaphone,
  MessageCircle,
  Mic,
  Moon,
  Music,
  Navigation,
  Newspaper,
  Palette,
  Phone,
  Pin,
  Plane,
  Radio,
  Rss,
  Search,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Store,
  Sun,
  Ticket,
  Tv,
  Umbrella,
  User,
  Users,
  Utensils,
  Video,
  Wifi,
} from 'lucide-react';

/** Lowercase ids stored in the database; maps to Lucide components (tree-shaken). */
export const NAV_ICON_MAP: Record<string, LucideIcon> = {
  home: Home,
  calendar: Calendar,
  newspaper: Newspaper,
  radio: Radio,
  images: Images,
  store: Store,
  'map-pin': MapPin,
  map: Map,
  users: Users,
  user: User,
  heart: Heart,
  star: Star,
  music: Music,
  mic: Mic,
  phone: Phone,
  mail: Mail,
  globe: Globe,
  'external-link': ExternalLink,
  search: Search,
  settings: Settings,
  shield: Shield,
  image: Image,
  camera: Camera,
  video: Video,
  tv: Tv,
  sparkles: Sparkles,
  gift: Gift,
  ticket: Ticket,
  flag: Flag,
  building: Building,
  'building-2': Building2,
  landmark: Landmark,
  coffee: Coffee,
  utensils: Utensils,
  car: Car,
  bus: Bus,
  plane: Plane,
  briefcase: Briefcase,
  'book-open': BookOpen,
  book: Book,
  'file-text': FileText,
  link: Link,
  'link-2': Link2,
  hash: Hash,
  'at-sign': AtSign,
  bell: Bell,
  megaphone: Megaphone,
  'message-circle': MessageCircle,
  rss: Rss,
  wifi: Wifi,
  code: Code,
  cloud: Cloud,
  sun: Sun,
  moon: Moon,
  umbrella: Umbrella,
  church: Church,
  compass: Compass,
  navigation: Navigation,
  pin: Pin,
  'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart,
  headphones: Headphones,
  palette: Palette,
  crown: Crown,
};

export type NavIconId = keyof typeof NAV_ICON_MAP;

const NAV_ICON_LABELS: Record<string, string> = {
  home: 'Home',
  calendar: 'Calendar / Events',
  newspaper: 'Newspaper / Blog',
  radio: 'Radio',
  images: 'Images / Gallery',
  store: 'Store / Directory',
  'map-pin': 'Map pin',
  map: 'Map',
  users: 'Users / Group',
  user: 'User',
  heart: 'Heart',
  star: 'Star',
  music: 'Music',
  mic: 'Microphone',
  phone: 'Phone',
  mail: 'Mail',
  globe: 'Globe / Web',
  'external-link': 'External link',
  search: 'Search',
  settings: 'Settings',
  shield: 'Shield',
  image: 'Image',
  camera: 'Camera',
  video: 'Video',
  tv: 'TV',
  sparkles: 'Sparkles',
  gift: 'Gift',
  ticket: 'Ticket',
  flag: 'Flag',
  building: 'Building',
  'building-2': 'Building (alt)',
  landmark: 'Landmark',
  coffee: 'Coffee',
  utensils: 'Food / Utensils',
  car: 'Car',
  bus: 'Bus',
  plane: 'Plane',
  briefcase: 'Briefcase',
  'book-open': 'Book open',
  book: 'Book',
  'file-text': 'Document',
  link: 'Link',
  'link-2': 'Link (alt)',
  hash: 'Hash',
  'at-sign': '@ Mention',
  bell: 'Bell / Notify',
  megaphone: 'Megaphone',
  'message-circle': 'Message',
  rss: 'RSS',
  wifi: 'Wi‑Fi',
  code: 'Code',
  cloud: 'Cloud',
  sun: 'Sun',
  moon: 'Moon',
  umbrella: 'Umbrella',
  church: 'Church',
  compass: 'Compass',
  navigation: 'Navigation',
  pin: 'Pin',
  'shopping-bag': 'Shopping bag',
  'shopping-cart': 'Cart',
  headphones: 'Headphones',
  palette: 'Palette / Art',
  crown: 'Crown / Founders',
};

/** Sorted options for the admin icon picker. */
export const NAV_LUCIDE_OPTIONS: { id: string; label: string }[] = Object.keys(NAV_ICON_MAP)
  .sort((a, b) => (NAV_ICON_LABELS[a] || a).localeCompare(NAV_ICON_LABELS[b] || b))
  .map((id) => ({ id, label: NAV_ICON_LABELS[id] || id }));

/**
 * Renders a header/footer nav icon.
 * - Lucide: id from NAV_ICON_MAP (e.g. `store`, `home`).
 * - Web Awesome / Font Awesome Free: `wa:icon-name` (classic) or `wa:brands:icon-name` (brands).
 *   Requires Web Awesome + `wa-icon` loaded (see index.html).
 */
export function renderNavIcon(icon: string | null | undefined, className = 'w-4 h-4'): ReactNode {
  const raw = (icon || '').trim();
  if (!raw) return null;

  if (raw.startsWith('wa:brands:')) {
    const name = raw.slice(10).trim();
    if (!name) return null;
    return createElement('wa-icon', {
      key: raw,
      family: 'brands',
      name,
      style: { fontSize: '1rem', display: 'inline-block', verticalAlign: 'middle', lineHeight: 1 },
      className,
    });
  }

  if (raw.startsWith('wa:')) {
    const name = raw.slice(3).trim();
    if (!name) return null;
    return createElement('wa-icon', {
      key: raw,
      name,
      style: { fontSize: '1rem', display: 'inline-block', verticalAlign: 'middle', lineHeight: 1 },
      className,
    });
  }

  const Icon = NAV_ICON_MAP[raw.toLowerCase()];
  if (!Icon) return null;
  return createElement(Icon, { className });
}

export function isNavPathActive(pathname: string, url: string): boolean {
  const u = (url || '').trim();
  if (!u) return false;
  if (u === '/') return pathname === '/';
  const normalized = u.length > 1 && u.endsWith('/') ? u.slice(0, -1) : u;
  if (pathname === normalized) return true;
  return pathname.startsWith(`${normalized}/`);
}
